import {
  AdditionalContact,
  HistoryAdditionalContact,
} from "../interfaces/additionalContact";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;
export const createAdditionalContact = async (
  additionalContact: AdditionalContact,
): Promise<AdditionalContact> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalContact/CreateAdditionalContact`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(additionalContact),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create additional contact.");
  }
  return response.json();
};

export const getAdditionalContacts = async (
  clientID: number,
): Promise<AdditionalContact[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalContact/GetAllAdditionalContacts/${clientID}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch additional contacts for ClientID ${clientID}.`,
    );
  }

  return response.json();
};

export const getAdditionalContact = async (
  additionalContactID: number,
): Promise<AdditionalContact> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalContact/GetAdditionalContact/${additionalContactID}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch additional contact with ID ${additionalContactID}.`,
    );
  }

  return response.json();
};

export const updateAdditionalContact = async (
  contact: AdditionalContact,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalContact/updateAdditionalContact/${contact.additionalContactID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(contact),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update contact with ID ${contact.additionalContactID}.`,
    );
  }
};

export const deleteAdditionalContact = async (
  additionalContactID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalContact/DeleteAdditionalContact/${additionalContactID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete additional contact with ID ${additionalContactID}.`,
    );
  }
};

export const getAdditionalContactsHistory = async (
  clientID: number,
): Promise<HistoryAdditionalContact[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalContact/GetAdditionalContactsHistory/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch additional contact history");
  }

  const data: HistoryAdditionalContact[] = await response.json();
  return data;
};
